/** Dropdown and context menus (Radix), styled per the design system's Menu component. */
import { ContextMenu as CM, DropdownMenu as DM } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';

const contentCls =
  'z-50 min-w-[200px] max-h-[min(70vh,var(--radix-dropdown-menu-content-available-height,480px))] overflow-y-auto scroll-thin rounded-md bg-raised p-1 text-ink shadow-menu outline-none data-[state=open]:animate-in';
const itemCls =
  'relative flex h-7 cursor-default select-none items-center gap-2 rounded-sm px-2 text-base outline-none data-[highlighted]:bg-surface-2 data-[disabled]:opacity-50';

export const Menu = DM.Root;
export const MenuTrigger = DM.Trigger;
export const MenuGroup = DM.Group;
export const MenuSub = DM.Sub;

export function MenuContent({
  className,
  align = 'start',
  sideOffset = 4,
  ...props
}: ComponentProps<typeof DM.Content>) {
  return (
    <DM.Portal>
      <DM.Content
        align={align}
        sideOffset={sideOffset}
        className={cn(contentCls, className)}
        {...props}
      />
    </DM.Portal>
  );
}

export interface MenuItemProps extends ComponentProps<typeof DM.Item> {
  icon?: ReactNode;
  shortcut?: string;
  destructive?: boolean;
}

export function MenuItem({
  icon,
  shortcut,
  destructive,
  className,
  children,
  ...props
}: MenuItemProps) {
  return (
    <DM.Item className={cn(itemCls, destructive && 'text-overdue', className)} {...props}>
      {icon && <span className="flex size-4 items-center justify-center text-ink-2">{icon}</span>}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {shortcut && <span className="text-cap text-ink-3">{shortcut}</span>}
    </DM.Item>
  );
}

export function MenuCheckItem({
  checked,
  icon,
  className,
  children,
  ...props
}: ComponentProps<typeof DM.CheckboxItem> & { icon?: ReactNode }) {
  return (
    <DM.CheckboxItem checked={checked} className={cn(itemCls, className)} {...props}>
      {icon && <span className="flex size-4 items-center justify-center">{icon}</span>}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      <DM.ItemIndicator>
        <Check className="size-4 text-primary" />
      </DM.ItemIndicator>
    </DM.CheckboxItem>
  );
}

export function MenuLabel({ className, ...props }: ComponentProps<typeof DM.Label>) {
  return <DM.Label className={cn('px-2 pt-1.5 pb-1 text-cap text-ink-3', className)} {...props} />;
}

export function MenuSeparator() {
  return <DM.Separator className="my-1 h-px bg-line" />;
}

export function MenuSubTrigger({
  icon,
  children,
  className,
}: {
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <DM.SubTrigger className={cn(itemCls, 'data-[state=open]:bg-surface-2', className)}>
      {icon && <span className="flex size-4 items-center justify-center text-ink-2">{icon}</span>}
      <span className="flex-1">{children}</span>
      <ChevronRight className="size-4 text-ink-3" />
    </DM.SubTrigger>
  );
}

export function MenuSubContent({ className, ...props }: ComponentProps<typeof DM.SubContent>) {
  return (
    <DM.Portal>
      <DM.SubContent sideOffset={4} className={cn(contentCls, className)} {...props} />
    </DM.Portal>
  );
}

// ---- context menu (right click) ----
export const ContextMenu = CM.Root;
export const ContextMenuTrigger = CM.Trigger;

export function ContextMenuContent({ className, ...props }: ComponentProps<typeof CM.Content>) {
  return (
    <CM.Portal>
      <CM.Content className={cn(contentCls, className)} {...props} />
    </CM.Portal>
  );
}

export function ContextMenuItem({
  icon,
  shortcut,
  destructive,
  className,
  children,
  ...props
}: ComponentProps<typeof CM.Item> & {
  icon?: ReactNode;
  shortcut?: string;
  destructive?: boolean;
}) {
  return (
    <CM.Item className={cn(itemCls, destructive && 'text-overdue', className)} {...props}>
      {icon && <span className="flex size-4 items-center justify-center text-ink-2">{icon}</span>}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {shortcut && <span className="text-cap text-ink-3">{shortcut}</span>}
    </CM.Item>
  );
}

export function ContextMenuSeparator() {
  return <CM.Separator className="my-1 h-px bg-line" />;
}
